<template>
    <b-modal
        ref="modalStatements"
        lazy
        title-tag="h2"
        :title="this.title"
        no-close-on-esc
        no-close-on-backdrop
        no-enforce-focus
    >
        <p v-if="isEmail">
            Statements will not be sent to clients who are missing email
            addresses or whose email addresses are invalid. Review your
            <a href="#" target="_blank">client list</a> to see which clients are
            missing email addresses.
        </p>

        <b-form-group
            label-for="period"
            label-cols="4"
            label="Statement Period:"
        >
            <b-select
                v-model="period"
                @change="changePeriodRange"
                :disabled="isLoading"
            >
                <option value="0">
                    All dates - The entire history for the client
                </option>
                <option value="1">
                    Custom - The date range selected by the clinic
                </option>
                <option value="2">
                    This week - The current week to date
                </option>
                <option value="3">
                    This month - The current calendar month
                </option>
                <option value="4">
                    This quarter - The current calendar quarter
                </option>
                <option value="5">
                    This year - The current calendar year
                </option>
                <option value="6">
                    Last week - The last calendar week
                </option>
                <option value="7">
                    Last month - The last calendar month
                </option>
                <option value="8">
                    Last quarter - The last calendar quarter
                </option>
                <option value="9">
                    Last year - The last calendar year
                </option>
            </b-select>

            <div class="dates-wrapper">
                <date-picker
                    :disabled="period === '0' || isLoading"
                    v-validate="'required'"
                    :config="datePickerOptions"
                    :class="{ 'border-danger': errors.has('periodStart') }"
                    name="periodStart"
                    v-model="periodStart"
                ></date-picker>
                <span>-</span>
                <date-picker
                    :disabled="period === '0' || isLoading"
                    v-validate="'required'"
                    :config="datePickerOptions"
                    :class="{ 'border-danger': errors.has('periodEnd') }"
                    name="periodEnd"
                    v-model="periodEnd"
                ></date-picker>
            </div>
        </b-form-group>

        <b-form-group
            label-for="detail"
            label-cols="4"
            label="Transaction Detail:"
        >
            <b-select v-model="transDetail" :disabled="isLoading">
                <option value="1">
                    Include transaction detail
                </option>
                <option value="0">
                    Do not include transaction detail
                </option>
            </b-select>
        </b-form-group>

        <b-form-group label-for="invoices" label-cols="4" label="Invoices:">
            <b-select v-model="invoices" :disabled="isLoading">
                <option value="0">
                    Do not include supporting invoices
                </option>
                <option value="2">
                    Include unpaid supporting invoices
                </option>
                <option value="1">
                    Include all supporting invoices
                </option>
            </b-select>
        </b-form-group>

        <b-form-group
            v-if="isEmail"
            label-for="subject"
            label-cols="4"
            label="Subject:"
        >
            <b-input
                :readonly="isLoadingEmailContent"
                :placeholder="isLoadingEmailContent ? 'Loading...' : 'Subject'"
                v-model="subject"
                :disabled="isLoading"
            />
        </b-form-group>

        <b-form-group
            v-if="isEmail"
            label-for="message"
            label-cols="4"
            label="Message:"
        >
            <tinymce
                :init="tinymceConfig"
                v-model="message"
                :readonly="isLoadingEmailContent"
                :placeholder="isLoadingEmailContent ? 'Loading...' : 'Message'"
            />
        </b-form-group>

        <b-form-group
            v-if="isEmail && showPayNow"
            label-for="pay"
            label-cols="4"
            label="Pay Online:"
        >
            <label class="checkbox">
                <input
                    type="checkbox"
                    v-model="isAllowPayOnline"
                    :disabled="isLoading"
                />
                Allow client to pay online
            </label>
        </b-form-group>

        <template v-slot:modal-footer="{ cancel }">
            <loader v-if="isLoading" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="cancel"
                v-if="!isLoading"
            >
                Cancel
            </b-button>

            <b-button
                v-if="!isLoading"
                variant="primary"
                @click="submit"
                :disabled="disableSubmitButton"
            >
                {{ labelSubmit }}
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { DateTime } from "luxon";
import Loader from "@/module/commons/loader";
import tinymce from "@tinymce/tinymce-vue";
import tinymceHelper from "@/module/commons/helper-tinymce"; // helper
import EXTENSIONS from "@/constants/extensions";

const EMAIL = "EMAIL";
const PRINT = "PRINT";

export default {
    components: {
        Loader,
        tinymce
    },
    data() {
        return {
            title: "",
            type: null,
            isAllowPayOnline: true,
            message: "",
            subject: "",
            invoices: "0",
            transDetail: "1",
            period: "3",
            constant: {
                EMAIL: EMAIL,
                PRINT: PRINT
            },
            datePickerOptions: {
                format: null,
                timeZone: ""
            },
            periodStart: null,
            periodEnd: null,
            isLoading: false,
            labelSubmit: "",
            isLoadingEmailContent: true,
            tinymceConfig: {
                ...tinymceHelper.core, // use spread operator to inject all vetter-related configs
                toolbar: false,
                plugins: tinymceHelper.plugins
            }
        };
    },
    computed: {
        showPayNow() {
            let checkExtensions =
                this._isExtensionEnabled(EXTENSIONS.CODE.CODE_CCONNECT) ||
                this._isExtensionEnabled(EXTENSIONS.CODE.CODE_GRAVITY) ||
                this._isExtensionEnabled(EXTENSIONS.CODE.CODE_STRIPE);
            return checkExtensions;
        },
        isEmail() {
            return this.type === EMAIL;
        },
        disableSubmitButton() {
            if (!this.periodStart || this.periodStart === "") {
                return true;
            }

            if (!this.periodEnd || this.periodEnd === "") {
                return true;
            }

            if (this.type === EMAIL && this.subject === "") {
                return true;
            }

            if (this.type === EMAIL && this.message === "") {
                return true;
            }

            return false;
        }
    },
    methods: {
        open(params = {}) {
            let dt = DateTime.local().setZone(this._timeZone);
            this.type = params.type;
            this.datePickerOptions.timeZone = this._timeZone;

            if (params.datePickerOptions && params.datePickerOptions.format) {
                this.datePickerOptions.format = params.datePickerOptions.format;
            } else {
                this.datePickerOptions.format = this.getDateFormat();
            }

            this.periodStart = dt
                .startOf("month")
                .endOf("day")
                .toFormat(this._dateFormat);

            this.periodEnd = dt.toFormat(this._dateFormat);

            if (params.type === EMAIL) {
                this.title = "Email Statements";
                this.labelSubmit = "Email";
                this.applyEmailContent();
            } else if (params.type === PRINT) {
                this.title = "Print Statements";
                this.labelSubmit = "Print";
            }

            this.$refs.modalStatements.show();
        },
        async applyEmailContent() {
            try {
                this.isLoadingEmailContent = true;

                let res = await this.$http.get(
                    process.env.BARRAMUNDI + "report/gen-email-subject-body"
                );

                if (res.data.response && res.data.response.resource) {
                    let { message, subject } = res.data.response.resource;
                    this.subject = subject;
                    this.message = message;
                }

                this.isLoadingEmailContent = false;
            } catch (error) {
                this.isLoadingEmailContent = false;
            }
        },
        async submit() {
            try {
                this.isLoading = true;

                let uri;
                let params = {
                    from: this.periodStart,
                    to: this.periodEnd,
                    client_ids: this.$store.state.report.accountsReceivable.selectedClientIds, // prettier-ignore
                    period: Number(this.period),
                    transaction: Number(this.transDetail),
                    invoices: Number(this.invoices),
                    timezone: this._timeZone,
                    pay_now: this.isAllowPayOnline ? 1 : 0
                };

                if (this.type === EMAIL) {
                    // extend params
                    params = {
                        ...params,
                        subject: this.subject,
                        message: this.message
                    };

                    uri = `${process.env.BARRAMUNDI}report/gen-statements-report-email/3`;
                } else if (this.type === PRINT) {
                    uri = `${process.env.BARRAMUNDI}report/gen-statements-report-print/3`;
                }

                let res = await this.$http.post(uri, params);

                if (res.data && res.data.mode === 0) {
                    this._message(res.data.message);
                } else {
                    throw new Error(res.data.message);
                }

                this.$refs.modalStatements.hide();
            } catch (error) {
                console.log(error);
                this._message(error.message, "danger");
                this.isLoading = false;
            }
        },
        changePeriodRange(rawValue) {
            let value = Number(rawValue);
            let dateTimeObject = DateTime.local().setZone(this._timeZone);

            switch (value) {
                case 2:
                    this.periodStart = dateTimeObject
                        .startOf("week")
                        .toJSDate();
                    this.periodEnd = dateTimeObject.endOf("week").toJSDate();
                    break;

                case 3:
                    this.periodStart = dateTimeObject
                        .startOf("month")
                        .toJSDate();
                    this.periodEnd = dateTimeObject.endOf("month").toJSDate();
                    break;

                case 4:
                    this.periodStart = dateTimeObject
                        .startOf("quarter")
                        .toJSDate();
                    this.periodEnd = dateTimeObject.endOf("quarter").toJSDate();
                    break;

                case 5:
                    this.periodStart = dateTimeObject
                        .startOf("year")
                        .toJSDate();
                    this.periodEnd = dateTimeObject.endOf("year").toJSDate();
                    break;

                case 6:
                    this.periodStart = dateTimeObject
                        .minus({ week: 1 })
                        .startOf("week")
                        .toJSDate();
                    this.periodEnd = dateTimeObject
                        .minus({ week: 1 })
                        .endOf("week")
                        .toJSDate();
                    break;

                case 7:
                    this.periodStart = dateTimeObject
                        .minus({ month: 1 })
                        .startOf("month")
                        .toJSDate();
                    this.periodEnd = dateTimeObject
                        .minus({ month: 1 })
                        .endOf("month")
                        .toJSDate();
                    break;

                case 8:
                    this.periodStart = dateTimeObject
                        .minus({ quarter: 1 })
                        .startOf("quarter")
                        .toJSDate();
                    this.periodEnd = dateTimeObject
                        .minus({ quarter: 1 })
                        .endOf("quarter")
                        .toJSDate();
                    break;

                case 9:
                    this.periodStart = dateTimeObject
                        .minus({ year: 1 })
                        .startOf("year")
                        .toJSDate();
                    this.periodEnd = dateTimeObject
                        .minus({ year: 1 })
                        .endOf("year")
                        .toJSDate();
                    break;

                default:
                    break;
            }
        }
    }
};
</script>

<style scoped>
label.checkbox {
    font-weight: normal !important;
    user-select: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    margin: 0;
    padding: 0;
}
label.checkbox input {
    margin-right: 5px;
}
::v-deep .form-row.form-group .col {
    justify-content: center;
    display: flex;
    flex-direction: column;
}
.message {
    height: 180px !important;
}
.dates-wrapper {
    display: grid;
    grid-template-columns: 1fr 12px 1fr;
    justify-content: center;
    align-items: center;
    text-align: center;
    margin-top: 3px;
}
</style>
